/* eslint-disable import/no-unresolved */
import React, { useEffect, useState } from "react";
import { func, number, arrayOf, bool, shape, string } from "prop-types";
import { connect } from "react-redux";
import { Box, Button, Agenda } from "@ui-kit/components/index.es";
import copy from "copy-to-clipboard";

import { selectors as transferSelectors, actions as transferActions } from "reducers/transfer";
import { selectors as sessionSelectors } from "reducers/session";
import { actions as agendaActions, selectors as agendaSelectors } from "reducers/frequentDestination";
import { actions as notificationActions } from "reducers/notification";

import * as i18n from "util/i18n";
import * as configUtils from "util/config";
import { push } from "react-router-redux";
import getTransferPath from "util/transfers";

const FORM_ID = "transfers.historic";
function TransfersAgenda(props) {
    TransfersAgenda.propTypes = {
        dispatch: func.isRequired,
        isDesktop: bool.isRequired,
        filters: shape({}).isRequired,
        activeEnvironment: shape({}).isRequired,
        idEnvironment: number.isRequired,
        listFrequentDestinations: arrayOf().isRequired,
        formikProps: shape({}).isRequired,
        setSearchedCBU: func.isRequired,
        searchedCBU: string.isRequired,
        isSameHolderTransfer: bool.isRequired,
        styleV3: bool,
        testId: string,
        inlineToggle: bool,
    };

    TransfersAgenda.defaultProps = {
        styleV3: false,
        testId: undefined,
        inlineToggle: false,
    };

    const {
        dispatch,
        isDesktop,
        filters,
        idEnvironment,
        listFrequentDestinations,
        formikProps,
        searchedCBU,
        setSearchedCBU,
        isSameHolderTransfer,
        styleV3,
        testId,
        inlineToggle,
    } = props;

    const [showAgenda, setShowAgenda] = useState(false);
    const [selectedContactId, setSelectedContactId] = useState(null);
    const showNewScheduleCards = configUtils.getBoolean("frontend.show.scheduleCards.functionalities");
    const newAgendaEnable = configUtils.getBoolean("frontend.show.NuevoComponenteAgenda.enabled", false);

    useEffect(() => {
        dispatch(agendaActions.loadListRequest(idEnvironment));
    }, [dispatch, idEnvironment]);

    const handleAddContact = () => {
        dispatch(agendaActions.resetListFrequentDestinations());
        dispatch(push(newAgendaEnable ? "/v2/agenda/create" : "/FrequentDestination/create/step1"));
    };

    const handleAgendaModify = (id) => {
        dispatch(agendaActions.modifyFrequentDestinationPreRequest(id, true));
    };

    const handleAgendaDelete = (id) => {
        dispatch(agendaActions.deleteFrequentDestinationPreviewRequest(id));
    };

    const handleAgendaShare = (contact) => {
        const { name, recipientName, cbu, recipientEmail, recipientDocumentNumber } = contact;
        const i18nParams = {
            reference: name,
            headline: recipientName,
            document: recipientDocumentNumber,
            account: cbu,
            email: recipientEmail || "",
        };
        const textCopy = i18n.get("transfer.frequentDestinations.textCopy", null, i18nParams);

        if (!isDesktop && window.cordova) {
            const options = {
                message: textCopy,
            };
            window.plugins.socialsharing.shareWithOptions(options, null, null);
        } else {
            copy(textCopy);
            dispatch(
                notificationActions.showNotification(i18n.get("frequentDestination.copyDate"), "success", [
                    "transfersHistoric",
                ]),
            );
        }
    };

    const handleAgendaFilter = (contact) => {
        const { recipientName, cbu } = contact;
        const { dateFrom, dateTo, reference, transferType, onlyPendings, dates } = filters;
        const filtersToSet = {
            dates: dates || [dateFrom, dateTo],
            dateTo,
            dateFrom,
            beneficiary: recipientName,
            reference,
            onlyPendings,
            onlyTransfers: true,
            transferType,
            minAmount: filters.minAmount?.amount || 0,
            maxAmount: filters.maxAmount?.amount || 0,
            hasSummitted: true,
            cbuCvu: showNewScheduleCards ? cbu : null,
            orderBy: "submit_date_time DESC",
            dateType: "submit_date_time",
            pageNumber: 1,
        };
        const filtersToSend = {
            ...filters,
            beneficiary: recipientName,
            cbuCvu: showNewScheduleCards ? cbu : null,
            hasSummitted: true,
            status: null,
            pageNumber: 1,
        };
        formikProps.setValues(filtersToSet);
        setSearchedCBU(cbu);
        dispatch(transferActions.setFiltersHistoric(filtersToSet));

        dispatch(transferActions.setSelectedChip("historic"));
        dispatch(transferActions.loadListRequest(filtersToSend, false, false));
        dispatch(transferActions.setShowFilters(true));
        dispatch(transferActions.setHasFilterChanged(true));
    };

    const handleContactSelect = (contact) => {
        const { activeEnvironment } = props;
        const { idFrequentDestination, isOwn, cbu, recipientEmail } = contact;

        const transferKind = isOwn === "1" ? "same" : "other";
        setSelectedContactId((prevSelectedId) => {
            if (prevSelectedId === idFrequentDestination) {
                return null;
            }
            return idFrequentDestination;
        });
        if (activeEnvironment.permissions.transferToContact) {
            dispatch(
                transferActions.loadThirdPartiesTransfer({
                    cbu,
                    email: recipientEmail,
                    transferKind,
                    isFrequentDestinations: true,
                }),
            );
            dispatch(push(getTransferPath("toContact")));
        }
    };

    const manageAgenda = () => {
        const { activeEnvironment } = props;
        return activeEnvironment.permissions["frequentDestination.manage"];
    };

    const agendaMenuOptions = (contact) => {
        const { idFrequentDestination } = contact;
        const items = [];
        if (manageAgenda()) {
            items.push(
                {
                    icon: "Pencil",
                    iconVariant: "outline",
                    label: i18n.get(`${FORM_ID}.agenda.modify`),
                    callback: () => {
                        handleAgendaModify(idFrequentDestination);
                    },
                },
                {
                    icon: "Trash",
                    iconVariant: "outline",
                    label: i18n.get(`${FORM_ID}.agenda.delete`),
                    callback: () => {
                        handleAgendaDelete(idFrequentDestination);
                    },
                },
            );
        }
        items.push(
            {
                icon: isDesktop && !window.cordova && newAgendaEnable ? "Pages" : "ShareAlt",
                iconVariant: "outline",
                label: i18n.get(
                    `${FORM_ID}.agenda.${isDesktop && !window.cordova && newAgendaEnable ? "copy" : "share"}`,
                ),
                callback: () => {
                    handleAgendaShare(contact);
                },
            },
            {
                icon: "Search",
                iconVariant: "outline",
                label: i18n.get(`${FORM_ID}.agenda.filter`),
                callback: () => {
                    handleAgendaFilter(contact);
                },
            },
        );
        return items;
    };

    const agendaItems = listFrequentDestinations.map((contact) => ({
        cbucvu: contact.cbu,
        isOwn: contact.isOwn,
        name: contact.recipientName,
        email: contact.recipientEmail,
        reference: contact.name,
        document: contact.recipientDocumentNumber,
        menuOptions: agendaMenuOptions(contact),
        isSelected: selectedContactId === contact.idFrequentDestination,
        onClick: () => handleContactSelect(contact),
    }));

    let agendaTogglePaddingBottom;
    if (showAgenda && !inlineToggle) {
        agendaTogglePaddingBottom = styleV3 ? 24 : 16;
    }

    return (
        // `display: contents` lets the toggle sit inline with sibling buttons
        // while the expanded agenda still wraps to its own full-width row.
        <Box css={inlineToggle ? { display: "contents" } : undefined}>
            <Box pt={inlineToggle ? undefined : 12} pb={agendaTogglePaddingBottom}>
                <Button
                    text={i18n.get(FORM_ID + (showAgenda ? ".hide.agenda" : ".show.agenda"))}
                    iconVariant="outline"
                    variant="outline"
                    icon={showAgenda ? "EyeOff" : "Eye"}
                    size="medium"
                    {...(styleV3 && {
                        rounded: true,
                        withShadow: true,
                        ...(inlineToggle ? {} : { css: { padding: 12 } }),
                    })}
                    onClick={() => setShowAgenda(!showAgenda)}
                />
            </Box>
            {showAgenda && (
                <Box
                    w={inlineToggle ? "100%" : undefined}
                    {...(styleV3 ? { p: 16, radius: 16, shadow: "default" } : {})}>
                    <Agenda
                        data-testid={testId}
                        styleV3={styleV3}
                        contactsArray={agendaItems}
                        id="AgendaElement"
                        idContainer="app"
                        showAddContact={manageAgenda()}
                        showSearcher
                        searchByContact={showNewScheduleCards ? searchedCBU : null}
                        showReference={showNewScheduleCards}
                        showSwitchSameTitular={showNewScheduleCards}
                        initialValueFilterbySameTitular={isSameHolderTransfer}
                        getFilterbySameTitular={(v) => dispatch(agendaActions.setSameHolderTransfer(v))}
                        onAddContact={() => {
                            handleAddContact();
                        }}
                    />
                </Box>
            )}
        </Box>
    );
}

const mapStateToProps = (state) => ({
    fetching: transferSelectors.isFetchingMore(state),
    activeEnvironment: sessionSelectors.getActiveEnvironment(state),
    listFrequentDestinations: agendaSelectors.getListFrequentDestinations(state),
    idEnvironment: agendaSelectors.getEnviromentId(state),
    filters: transferSelectors.getFilters(state),
    isSameHolderTransfer: agendaSelectors.getSameHolderTransfer(state),
});
export default connect(mapStateToProps)(TransfersAgenda);
